/**
 * Victory card drops (HEX-63 / ADR-0015 §6).
 *
 * After a player wins an encounter, the slain enemy may drop one card. Two rolls
 * off a seeded `Rng`:
 *   1. *Whether* a card drops — `DROP_CHANCE[tier]`. Common encounters rarely
 *      drop; legendary always drops ≥1 (chance 1).
 *   2. *Which rarity* — `DROP_RARITY_WEIGHTS[tier]`, a distribution bounded by
 *      the encounter tier (a common enemy never drops above common; a rare never
 *      above rare; …). The card itself is then picked uniformly from the
 *      authored gameplay pool of that rarity.
 *
 * Pure + deterministic given the `Rng`, so the weights are testable by
 * probability band (project test floor). Only the §8 gameplay pool is eligible —
 * `die` fuel and the starter-only Reroll are excluded, so drops can never hand
 * out a pool-fuel card or duplicate the free starter utility.
 */

import { ATTACK_CARDS, DEFENSE_CARDS, RECURSIVE_CARDS, SUPPORT_CARDS, TRAP_CARDS } from './cards';
import type { Rng } from './dice';
import type { Card, EnemyTier, Rarity } from './types';

/** The §8 30-card gameplay pool (no `die` fuel, no starter-only cards). */
export const GAMEPLAY_CARDS: readonly Card[] = [
  ...ATTACK_CARDS,
  ...DEFENSE_CARDS,
  ...SUPPORT_CARDS,
  ...RECURSIVE_CARDS,
  ...TRAP_CARDS,
];

/** Probability that *a* card drops, by encounter tier (ADR-0015 §6). Legendary
 * always drops; common is an occasional bonus (HEX-72: 0.15 → 0.25). The drop is
 * then offered as card-or-gold at the prompt (HEX-72). First-pass — tune in
 * playtest. */
export const DROP_CHANCE: Record<EnemyTier, number> = {
  common: 0.25,
  rare: 0.4,
  epic: 0.7,
  legendary: 1,
};

/** Rarity distribution of a dropped card, bounded by encounter tier (ADR-0015
 * §6 — the dropped rarity never exceeds the tier). Weights per tier sum to 1.
 * First-pass — tune in playtest. */
export const DROP_RARITY_WEIGHTS: Record<EnemyTier, Partial<Record<Rarity, number>>> = {
  common: { common: 1 },
  rare: { common: 0.7, rare: 0.3 },
  epic: { common: 0.4, rare: 0.4, epic: 0.2 },
  legendary: { common: 0.2, rare: 0.35, epic: 0.3, legendary: 0.15 },
};

const RARITY_ORDER: readonly Rarity[] = ['common', 'rare', 'epic', 'legendary'];

/** Weighted rarity pick for `tier`. Falls back to the heaviest entry if a float
 * rounding gap leaves `r` past the cumulative sum. */
function pickRarity(tier: EnemyTier, rng: Rng): Rarity {
  const weights = DROP_RARITY_WEIGHTS[tier];
  const r = rng();
  let cumulative = 0;
  let last: Rarity = 'common';
  for (const rarity of RARITY_ORDER) {
    const w = weights[rarity];
    if (w === undefined || w <= 0) continue;
    last = rarity;
    cumulative += w;
    if (r < cumulative) return rarity;
  }
  return last;
}

/**
 * Roll a victory card drop for an encounter of `tier`. Returns the dropped card
 * (a shared authored definition — the caller mints an owned instance id when it
 * adds the card to an inventory), or `null` when nothing drops. Consumes up to
 * two `rng()` draws (drop check, then rarity + card index).
 */
export function rollCardDrop(tier: EnemyTier, rng: Rng): Card | null {
  if (rng() >= DROP_CHANCE[tier]) return null;
  const rarity = pickRarity(tier, rng);
  const pool = GAMEPLAY_CARDS.filter((c) => c.rarity === rarity);
  if (pool.length === 0) return null;
  const index = Math.min(pool.length - 1, Math.floor(rng() * pool.length));
  return pool[index] ?? null;
}
